import { Particle } from '../ParticleLayer';

// GLOBAL SPRITE CACHE FOR DOUBLE CROSS EFFECT
const doubleCrossSpriteCache = new Map<string, HTMLCanvasElement>();

function getDCSteamSprite(color: string, size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `steam-${color}-${qSize}`;
    let sprite = doubleCrossSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, `rgba(${r},${g},${b},0.4)`);
            grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        doubleCrossSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getDCFireSprite(color: string, size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `fire-${color}-${qSize}`;
    let sprite = doubleCrossSpriteCache.get(key);

    if (!sprite) {
        const drawRadius = qSize * 1.5;
        sprite = document.createElement('canvas');
        sprite.width = drawRadius * 2;
        sprite.height = drawRadius * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = drawRadius;
            const centerY = drawRadius;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, color);
            grad.addColorStop(0.6, color);
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, drawRadius, 0, Math.PI * 2);
            sCtx.fill();
        }
        doubleCrossSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getDCSparkSprite(color: string, size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `spark-${color}-${qSize}`;
    let sprite = doubleCrossSpriteCache.get(key);

    if (!sprite) {
        const padding = 6;
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2 + padding * 2;
        sprite.height = qSize * 2 + padding * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;

            sCtx.fillStyle = color;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();

            sCtx.shadowBlur = 5;
            sCtx.shadowColor = color;
            sCtx.fill();
            sCtx.shadowBlur = 0;
        }
        doubleCrossSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type DoubleCrossEffectData = {
    centerX: number;
    centerY: number;

    // Fire Particles
    particles: {
        x: number;
        y: number;
        vx: number;
        vy: number;
        life: number;
        maxLife: number;
        size: number;
        color: string;
        angle: number; // For orbital motion
        radius: number; // Distance from center
        riseSpeed: number;
        type: 'fire' | 'steam' | 'spark';
    }[];

    // Flame Tongues (Angular sectors where fire is more intense)
    tongues: {
        angle: number;
        speed: number;
        intensity: number;
    }[];
}

export type DoubleCrossEffect = {
    kind: 'doubleCross';
    t: number;
    data: DoubleCrossEffectData;
}

export function createDoubleCrossEffect(x: number, y: number): DoubleCrossEffect {
    return {
        kind: 'doubleCross',
        t: 0,
        data: {
            centerX: x,
            centerY: y,
            particles: [],
            tongues: [
                { angle: 0, speed: 0.02, intensity: 1.0 },
                { angle: 2.1, speed: 0.03, intensity: 0.8 },
                { angle: 4.2, speed: 0.015, intensity: 1.2 }
            ]
        }
    };
}

export function renderDoubleCrossEffect(
    effect: DoubleCrossEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean {
    const TOTAL_TIME = 3000;
    const df = (effect as any)._df ?? 1;
    const t = effect.t;
    const data = effect.data;

    if (t > TOTAL_TIME) return false;

    // --- SPAWN PARTICLES ---
    // Spawn rate decreases over time
    const spawnCount = t < 2000 ? 6 : 0;

    // Update Tongues
    data.tongues.forEach(tongue => {
        tongue.angle += tongue.speed * df;
    });

    for (let i = 0; i < Math.round(spawnCount * df); i++) {
        // Pick a tongue to spawn near
        const tongue = data.tongues[Math.floor(Math.random() * data.tongues.length)];
        const angleSpread = 0.6; // Width of the tongue
        const angle = tongue.angle + (Math.random() - 0.5) * angleSpread;

        // Spiral spawn - Wider for prepared area
        const spawnRadius = 40 + Math.random() * 140;

        // Dark Purple & Dark Red Palette
        const fireColors = ['#2e1065', '#3b0764', '#450a0a', '#7f1d1d', '#581c87', '#991b1b'];
        const colorIdx = Math.floor(Math.random() * fireColors.length);

        // 1. Fire Particle
        data.particles.push({
            x: data.centerX + Math.cos(angle) * spawnRadius,
            y: data.centerY + Math.sin(angle) * spawnRadius * 0.4, // Elliptical base
            vx: 0,
            vy: 0,
            life: 600 + Math.random() * 800,
            maxLife: 600 + Math.random() * 800,
            size: 6 + Math.random() * 10, // Bigger particles
            color: fireColors[colorIdx],
            angle: angle,
            radius: spawnRadius,
            riseSpeed: 2 + Math.random() * 3,
            type: 'fire'
        });

        // 2. Steam Particle (Layering)
        if (Math.random() < 0.4) {
            const steamColors = ['#2e1065', '#4a044e', '#450a0a'];
            data.particles.push({
                x: data.centerX + Math.cos(angle) * spawnRadius,
                y: data.centerY + Math.sin(angle) * spawnRadius * 0.4,
                vx: (Math.random() - 0.5) * 0.5,
                vy: -1 - Math.random() * 1,
                life: 1000 + Math.random() * 1000,
                maxLife: 1000 + Math.random() * 1000,
                size: 20 + Math.random() * 30, // Large steam
                color: steamColors[Math.floor(Math.random() * steamColors.length)],
                angle: angle,
                radius: spawnRadius,
                riseSpeed: 0.5 + Math.random() * 1,
                type: 'steam'
            });
        }

        // 3. Spark Particle (NEW - Vibrant)
        if (Math.random() < 0.5) {
            // Vibrant colors for sparks
            const sparkColors = ['#d8b4fe', '#a855f7', '#fca5a5', '#ef4444', '#ffffff'];
            data.particles.push({
                x: data.centerX + Math.cos(angle) * (spawnRadius * 0.8), // Slightly inner
                y: data.centerY + Math.sin(angle) * (spawnRadius * 0.8) * 0.4,
                vx: (Math.random() - 0.5) * 2, // Jittery
                vy: -2 - Math.random() * 3,   // Fast rise
                life: 400 + Math.random() * 400,
                maxLife: 400 + Math.random() * 400,
                size: 1 + Math.random() * 2, // Small
                color: sparkColors[Math.floor(Math.random() * sparkColors.length)],
                angle: angle,
                radius: spawnRadius * 0.8,
                riseSpeed: 3 + Math.random() * 2,
                type: 'spark'
            });
        }
    }

    // --- UPDATE PARTICLES ---
    for (let i = data.particles.length - 1; i >= 0; i--) {
        const p = data.particles[i];
        p.life -= 16.7 * df;
        if (p.life <= 0) {
            data.particles[i] = data.particles[data.particles.length - 1];
            data.particles.pop();
            continue;
        }

        if (p.type === 'fire') {
            const orbitalSpeed = 0.03 * (150 / (p.radius + 10));
            p.angle += orbitalSpeed * df;
            const noise = Math.sin(t * 0.005 + p.angle * 3);
            p.radius += noise * 0.5 * df;
            p.y -= p.riseSpeed * df;
            p.x = data.centerX + Math.cos(p.angle) * p.radius;
            p.x += Math.sin(t * 0.01 + p.y * 0.05) * 5;
            p.size *= Math.pow(0.96, df);
        } else if (p.type === 'steam') {
            p.y -= p.riseSpeed * df;
            p.x += Math.sin(t * 0.002 + p.y * 0.01) * 0.5 * df;
            p.size += 0.1 * df;
        } else if (p.type === 'spark') {
            p.y -= p.riseSpeed * df;
            p.x += (p.vx + Math.sin(t * 0.02 + p.y * 0.1) * 2) * df;
            p.size *= Math.pow(0.95, df);
        }
    }

    // --- RENDER ---
    ctx.save();

    // Sort particles: Steam -> Fire -> Sparks
    data.particles.sort((a, b) => {
        const typeScore = (type: string) => type === 'steam' ? 0 : (type === 'fire' ? 1 : 2);
        const scoreA = typeScore(a.type);
        const scoreB = typeScore(b.type);
        if (scoreA !== scoreB) return scoreA - scoreB;
        return a.y - b.y;
    });

    data.particles.forEach(p => {
        const lifeRatio = p.life / p.maxLife;

        if (p.type === 'steam') {
            ctx.globalAlpha = lifeRatio * 0.3;
            ctx.globalCompositeOperation = 'source-over';
            const steamSprite = getDCSteamSprite(p.color, p.size);
            ctx.drawImage(steamSprite, p.x - p.size, p.y - p.size);
        } else if (p.type === 'fire') {
            ctx.globalAlpha = lifeRatio;
            ctx.globalCompositeOperation = 'source-over';
            const fireSprite = getDCFireSprite(p.color, p.size);
            const drawRadius = p.size * 1.5;
            ctx.drawImage(fireSprite, p.x - drawRadius, p.y - drawRadius);
        } else if (p.type === 'spark') {
            ctx.globalAlpha = lifeRatio;
            ctx.globalCompositeOperation = 'screen';
            const sparkSprite = getDCSparkSprite(p.color, p.size);
            const offset = (sparkSprite.width / 2); // qSize + padding
            ctx.drawImage(sparkSprite, p.x - offset, p.y - offset);
        }
    });

    // Draw "Core" Ring (Energy source) - Darker
    const ringLife = Math.min(1, t / 500) * Math.min(1, (TOTAL_TIME - t) / 500);
    if (ringLife > 0) {
        ctx.globalAlpha = ringLife * 0.6; // Slightly more opaque for dark colors
        ctx.globalCompositeOperation = 'source-over'; // Keep it dark
        ctx.strokeStyle = '#4a044e'; // Dark Fuchsia/Purple (Less Blue)
        ctx.lineWidth = 4;

        ctx.beginPath();
        ctx.ellipse(data.centerX, data.centerY, 160, 60, 0, 0, Math.PI * 2);
        ctx.stroke();

        // Inner spinning ring
        ctx.beginPath();
        ctx.ellipse(data.centerX, data.centerY, 140, 50, t * 0.003, 0, Math.PI * 2);
        ctx.strokeStyle = '#7f1d1d'; // Dark Red
        ctx.stroke();
    }

    ctx.restore();

    return true;
}
